/**
 * Shared vocabulary for Dimples.
 *
 * Coordinate convention, used everywhere without exception:
 *   +X = lateral, to the golfer's right as the camera sees it
 *   +Y = up
 *   +Z = downrange, toward the flag
 * The tee sits at the origin. Units are metres, seconds, radians.
 *
 * Two clocks exist. The simulation runs on *scaled* time (slow-mo, hitstop);
 * camera shake, particles, UI animation and audio run on *unscaled* time.
 * Frozen particles during hitstop read as a bug, not as juice.
 */

export type Vec3 = { x: number; y: number; z: number }

export const v3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z })

/** Quality of the strike, decided entirely by where the ball was at impact. */
export type ContactTier = 'FLUSH' | 'GOOD' | 'THIN' | 'SHANK' | 'WHIFF'

/** Ground types the ball can meet. `tee` is the mat around the origin. */
export type Surface = 'tee' | 'fairway' | 'rough' | 'green' | 'cartpath' | 'bunker' | 'water' | 'ob'

export type SwingStage = 'ADDRESS' | 'BACKSWING' | 'TOP' | 'DOWNSWING' | 'FOLLOW' | 'RESET'

export type Phase = 'LOBBY' | 'AIM' | 'TEE' | 'FLIGHT' | 'ROLLOUT' | 'RESULT' | 'DAY_END'

/** What the ball is doing on the tee. */
export type TeeMotion = 'REST' | 'SQUATTING' | 'AIRBORNE'

/**
 * How the ball is sitting, and therefore what it can do about it.
 *
 * The lie is the whole reason to care where a shot finishes. It works by moving
 * two numbers against each other: how far above you the clubface passes, and
 * how high you can jump. Deep grass holds you down until the sweet spot is
 * simply out of reach — so a bad lie does not make the timing harder, it lowers
 * your ceiling. You can be perfect and still only manage a GOOD.
 */
export type LieSpec = {
	name: string
	/** Where the ball sits: on a peg, on turf, or half-buried. */
	restY: number
	/** How far above that the clubface sweet spot passes. */
	sweetRise: number
	/** Scales hop launch speed. Grass and sand hold you down. */
	hopMult: number
	/** Turf steals power a tee does not. */
	speedMult: number
	/** Degrees of extra offline the lie itself contributes. */
	offlineDeg: number
	/** Sand and rough pop the ball up; concrete keeps it down. */
	launchDeg: number
	/** Sticks to you and bends every later flight. */
	mud: number
	/** Hard ceiling on launch angle. A putt rolls; it does not fly. */
	maxLaunchDeg?: number
	/**
	 * Scales backspin off this lie. A putter imparts almost none — left at full,
	 * the green's bite rule reads a putt as a checked wedge and spins it
	 * backwards, so putts crawl away from the hole instead of toward it.
	 */
	spinMult?: number
}

export type GolferSpec = {
	id: string
	name: string
	/** Multiplies launch speed. */
	speedMult: number
	/** Baseline aim error, degrees (1 sigma), before anger widens it. */
	dispersionDeg: number
	/** Scales every contact-tier threshold. Dana's big sweet spot is 1.6. */
	sweetSpotMult: number
	/** Scales the storm-off probability. Patient golfers are below 1. */
	temperMult: number
	/** Scales swing durations. Below 1 = quicker, harder to read. */
	tempoMult: number
	/**
	 * How much the swing tempo wanders *per swing*, 0..1.
	 *
	 * This is the drunk's whole mechanic. It attacks the one thing an experienced
	 * player leans on — the learned rhythm — while leaving the three-beat audio
	 * telegraph completely honest. You cannot play him from muscle memory, but
	 * you can always play him by listening. Unreliable, never unfair.
	 */
	chaos: number
	/** Polo colour, low-poly cast variety by swap. */
	color: number
	/** Body build: 0 = small, 1 = average, 2 = round. */
	build: 0 | 1 | 2
}

export type AngerTier = {
	name: string
	/** Multiplies launch speed. */
	power: number
	/** Added aim error in degrees (1 sigma). */
	dispersionDeg: number
	/** Downswing duration in seconds. Compression *is* the difficulty curve. */
	downswing: number
	/** Probability that dodging *from* this tier makes the golfer walk off. */
	stormOff: number
}

/** Everything the strike resolver decides in one frame. */
export type Contact = {
	tier: ContactTier
	/** Signed metres: ball centre minus clubface sweet spot. Negative = struck low. */
	offset: number
	/** 0..1, how central the strike was. Drives every juice layer. */
	quality: number
	/** Multiplies launch speed. */
	smash: number
	/** Degrees offline from the strike itself, before the golfer's aim error. */
	offlineDeg: number
	/** Backspin, dimensionless. Thin strikes barely spin; sky-balls spin hugely. */
	spinBack: number
	damage: number
}

export type LaunchParams = {
	speed: number
	/** Degrees above horizontal. */
	launchDeg: number
	/** Degrees offline; positive pushes toward +X. */
	headingDeg: number
	spinBack: number
	spinSide: number
}

export type FlightEventKind =
	| 'bounce'
	| 'roll'
	| 'rest'
	| 'splash'
	| 'skim'
	| 'skip'
	| 'tree'
	| 'apex'
	| 'ob'
	| 'holed'

export type FlightEvent = {
	kind: FlightEventKind
	surface: Surface
	/** Impact speed in m/s where it makes sense, else 0. */
	speed: number
	pos: Vec3
}

export type FlightState = {
	pos: Vec3
	vel: Vec3
	spinBack: number
	spinSide: number
	/** True once the ball has stopped for good. */
	resting: boolean
	rolling: boolean
	bounces: number
	surface: Surface
	/** Downrange distance at the first ground contact. */
	carry: number
	/** Total downrange distance travelled. */
	total: number
	maxHeight: number
	nudgesLeft: number
	/** True while the player is holding the ball down through its descent. */
	diving: boolean
	/** Bounces stolen with a well-timed tap. */
	skips: number
	passedApex: boolean
	/** Accumulated damage from this flight (cart path, plugging, and so on). */
	damage: number
	holed: boolean
}

export type ShotResult = {
	tier: ContactTier
	carry: number
	total: number
	surface: Surface
	distanceToPin: number
	score: number
	/** Ducks survived against this golfer, folded into the score. */
	nerve: number
	holed: boolean
	damage: number
}

/** A rectangle of ground, in world XZ. */
export type Rect = { x0: number; x1: number; z0: number; z1: number }
/** A circle of ground, in world XZ. */
export type Disc = { x: number; z: number; r: number }
/** A tree: a trunk you can hit and a canopy you have to get over or round. */
export type Tree = { x: number; z: number; r: number; h: number }

/**
 * One hole of the course.
 *
 * The corridor runs from the tee toward the pin, and the fairway is defined
 * along that line rather than along +Z, so a green set off to one side bends
 * the hole. Everything the flight model needs to know about the ground lives
 * here, so switching holes is a single assignment rather than a rebuild.
 */
export type HoleSpec = {
	no: number
	name: string
	par: number
	tee: { x: number; z: number }
	pin: { x: number; z: number }
	green: Disc
	/** Half-width of the mown corridor, and of the rough beyond it. */
	fairwayHalfW: number
	roughHalfW: number
	water: Rect[]
	bunkers: Disc[]
	cartpath: Rect | null
	trees: Tree[]
	/** Metres per second, and the compass direction it blows toward, in degrees. */
	wind: { speed: number; dirDeg: number }
}
